import { Button, Form, Input, Space, Row, Col } from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { isValidArray } from '../../../../utils/helpers'
import styles from '../../../../testrun.module.css'

const { TextArea } = Input

/** Shared text rules: no spaces-only, no leading/trailing spaces */
function textFieldRules(fieldLabel, { required = false, max } = {}) {
    const rules = []

    if (required) {
        rules.push({
            required: true,
            message: `Please enter ${fieldLabel.toLowerCase()}`,
        })
    }

    rules.push({
        validator: (_, value) => {
            if (value == null || value === '') {
                return Promise.resolve()
            }
            if (typeof value !== 'string') {
                return Promise.resolve()
            }
            if (!value.trim()) {
                return Promise.reject(
                    new Error(
                        `${fieldLabel} cannot be only spaces. Please enter valid text.`
                    )
                )
            }
            if (value !== value.trim()) {
                return Promise.reject(
                    new Error(
                        `Please remove extra spaces at the start or end of ${fieldLabel.toLowerCase()}`
                    )
                )
            }
            return Promise.resolve()
        },
    })

    if (max != null) {
        rules.push({
            max,
            message: `${fieldLabel} must be ${max} characters or less`,
        })
    }

    return rules
}

function ExportReportForm(props) {
    const {
        setOpen = () => {},
        onExport = () => {},
        exporting = false,
        reportId = '',
    } = props

    const [form] = Form.useForm()

    const handleSubmit = async (values) => {
        const clean = (v) =>
            typeof v === 'string' ? v.trim() : v == null ? '' : String(v)

        const finalAttachments = isValidArray(values?.attachments)
            ? values.attachments
                  .map((item) => ({
                      name: clean(item?.name),
                      url: clean(item?.url),
                  }))
                  .filter((item) => item.name !== '' && item.url !== '')
            : []

        const payload = {
            reportId,
            reportName: clean(values?.name),
            notes: clean(values?.note),
            attachments: finalAttachments,
        }
        onExport(payload)
    }

    return (
        <Form
            form={form}
            layout="vertical"
            className={styles.exportForm}
            onFinish={handleSubmit}
            requiredMark={false}
            initialValues={{
                name: '',
                note: '',
                attachments: [{ name: '', url: '' }],
            }}
        >
            <Form.Item
                label="Report Name"
                name="name"
                rules={textFieldRules('Report name', {
                    required: true,
                    max: 120,
                })}
            >
                <Input
                    className={styles.exportFieldControl}
                    placeholder="Enter title"
                    allowClear
                />
            </Form.Item>

            <Form.Item
                label="Note"
                name="note"
                rules={textFieldRules('Note')}
            >
                <TextArea
                    className={styles.exportFieldTextarea}
                    placeholder="Enter note"
                    autoSize={{ minRows: 2, maxRows: 8 }}
                />
            </Form.Item>

            <p className={styles.exportAttachmentsLabel}>Reference Link</p>

            <Form.List name="attachments">
                {(fields, { add, remove }) => (
                    <Space
                        direction="vertical"
                        style={{ width: '100%' }}
                        size={12}
                    >
                        {fields.map(({ key, name, ...restField }, idx) => (
                            <div key={key}>
                                <Row gutter={[12, 8]} align="top">
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'name']}
                                            label="Name"
                                            rules={textFieldRules('Name', {
                                                max: 100,
                                            })}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input
                                                className={
                                                    styles.exportFieldControl
                                                }
                                                placeholder="Name"
                                                allowClear
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={14}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'url']}
                                            label="URL"
                                            rules={[
                                                ...textFieldRules('URL'),
                                                {
                                                    type: 'url',
                                                    message:
                                                        'Please enter a valid URL (include http/https)',
                                                },
                                            ]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input
                                                className={
                                                    styles.exportFieldControl
                                                }
                                                placeholder="https://example.com"
                                                allowClear
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col
                                        xs={24}
                                        md={2}
                                        className={styles.exportAttachmentRemoveCol}
                                    >
                                        {idx > 0 && (
                                            <Button
                                                title="Remove"
                                                danger
                                                type="text"
                                                icon={<MinusCircleOutlined />}
                                                onClick={() => remove(name)}
                                                aria-label="Remove row"
                                                className={
                                                    styles.exportAttachmentRemoveBtn
                                                }
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        ))}

                        <Button
                            type="link"
                            className={styles.linkButton}
                            onClick={() => add({ name: '', url: '' })}
                            icon={<PlusOutlined />}
                        >
                            Add more
                        </Button>
                    </Space>
                )}
            </Form.List>

            <div className={styles.formActions}>
                <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setOpen(false)}
                    disabled={exporting}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className={`${styles.submitButton} ${
                        exporting ? styles.submitButtonDisabled : ''
                    }`}
                    disabled={exporting}
                >
                    {exporting ? 'Exporting...' : 'Export'}
                </button>
            </div>
        </Form>
    )
}

export default ExportReportForm
